import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Film, Package, FileText, LogOut, BarChart3, FolderOpen, Building2, ChevronLeft, ChevronRight, Menu, X, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import AdminFilmsTab       from '../components/admin/AdminFilmsTab';
import AdminArmoryTab      from '../components/admin/AdminArmoryTab';
import AdminBlogTab        from '../components/admin/AdminBlogTab';
import AdminAssetsTab      from '../components/admin/AdminAssetsTab';
import AdminStudioTab      from '../components/admin/AdminStudioTab';
import AdminDashboardTab   from '../components/admin/AdminDashboardTab';
import AdminSubmissionsTab from '../components/admin/AdminSubmissionsTab';

const LOGO_URL = "https://customer-assets-eiarnc6j.emergentagent.net/job_d237abdd-72c8-4f34-8f42-33e3f6f9671f/artifacts/3424abaf1deba85e_SWP_Transparent_Black_Logo_2026.png";

const TABS = [
  { id: 'dashboard',    label: 'Dashboard',    Icon: BarChart3  },
  { id: 'submissions',  label: 'Submissions',  Icon: Inbox      },
  { id: 'films',        label: 'Films',        Icon: Film       },
  { id: 'armory',       label: 'Resources',    Icon: Package    },
  { id: 'blog',         label: 'Blog',         Icon: FileText   },
  { id: 'assets',       label: 'Assets',       Icon: FolderOpen },
  { id: 'studio',       label: 'Studio',       Icon: Building2  },
];

const LEGACY = {
  analytics: 'dashboard', activity: 'dashboard',
  newsletter: 'studio', 'email-templates': 'studio',
  investors: 'studio', 'site-settings': 'studio', 'studio-portal': 'studio',
};

const s = {
  shell: {
    display: 'flex', minHeight: '100vh',
    background: 'var(--swp-black)', color: 'var(--swp-white)',
    fontFamily: 'var(--font-body)',
  },
  sidebar: (collapsed) => ({
    width: collapsed ? '64px' : '220px',
    flexShrink: 0,
    background: 'rgba(13,15,20,0.95)',
    backdropFilter: 'blur(16px)',
    borderRight: '0.5px solid rgba(255,255,255,0.07)',
    flexDirection: 'column',
    position: 'sticky', top: 0, height: '100vh',
    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
  }),
  sidebarTop: {
    padding: '20px 16px 16px',
    borderBottom: '0.5px solid rgba(255,255,255,0.06)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  navItem: (active, collapsed) => ({
    display: 'flex', alignItems: 'center',
    gap: collapsed ? 0 : '12px',
    padding: collapsed ? '12px 0' : '10px 16px',
    margin: collapsed ? '2px 8px' : '2px 10px',
    justifyContent: collapsed ? 'center' : 'flex-start',
    cursor: 'pointer', border: 'none',
    background: active ? 'rgba(106,157,190,0.12)' : 'transparent',
    borderRadius: '10px',
    color: active ? 'var(--swp-ice)' : 'rgba(238,240,242,0.38)',
    fontFamily: 'var(--font-mono)',
    fontSize: '10px', letterSpacing: '0.13em', textTransform: 'uppercase',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    width: collapsed ? 'calc(100% - 16px)' : 'calc(100% - 20px)',
    whiteSpace: 'nowrap',
    position: 'relative',
    ...(active && { boxShadow: '0 0 12px rgba(106,157,190,0.08)' }),
  }),
  navIcon: (active) => ({
    flexShrink: 0,
    transition: 'transform 0.2s ease',
    ...(active && { filter: 'drop-shadow(0 0 4px rgba(106,157,190,0.3))' }),
  }),
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  topbar: {
    height: '56px', padding: '0 32px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: '0.5px solid rgba(255,255,255,0.07)',
    background: 'rgba(13,15,20,0.8)',
    backdropFilter: 'blur(12px)',
    position: 'sticky', top: 0, zIndex: 40,
  },
  content: { flex: 1, padding: 'clamp(16px, 3vw, 36px) clamp(12px, 3vw, 32px)', overflowX: 'auto', overflowY: 'auto' },
};

const AdminDashboard = ({ onLogout }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const tabFromUrl = searchParams.get('tab') || 'dashboard';
  const activeTab  = LEGACY[tabFromUrl] || tabFromUrl;

  const handleTabChange = (id) => setSearchParams({ tab: id });

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    onLogout();
    navigate('/admin');
    toast.success('Logged out');
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'submissions': return <AdminSubmissionsTab />;
      case 'films':       return <AdminFilmsTab />;
      case 'armory':      return <AdminArmoryTab />;
      case 'blog':        return <AdminBlogTab />;
      case 'assets':      return <AdminAssetsTab />;
      case 'studio':      return <AdminStudioTab />;
      default:            return <AdminDashboardTab />;
    }
  };

  const activeLabel = TABS.find(t => t.id === activeTab)?.label || 'Dashboard';

  return (
    <div style={s.shell}>
      {/* Sidebar — desktop only */}
      <aside className="hidden lg:flex" style={s.sidebar(collapsed)}>
        <div style={s.sidebarTop}>
          {!collapsed && (
            <img src={LOGO_URL} alt="SWP" style={{ height: '32px', width: 'auto', opacity: 0.85 }} />
          )}
          <button onClick={() => setCollapsed(!collapsed)} style={{ background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '8px', cursor: 'pointer', color: 'rgba(238,240,242,0.35)', padding: '6px', marginLeft: collapsed ? 'auto' : 0, marginRight: collapsed ? 'auto' : 0, transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(106,157,190,0.1)'; e.currentTarget.style.color = 'var(--swp-ice)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = 'rgba(238,240,242,0.35)'; }}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        <nav style={{ flex: 1, padding: '12px 0', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              style={s.navItem(activeTab === id, collapsed)}
              title={collapsed ? label : undefined}
              onMouseEnter={e => {
                if (activeTab !== id) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                  e.currentTarget.style.color = 'rgba(238,240,242,0.7)';
                  e.currentTarget.style.transform = 'translateX(2px)';
                }
              }}
              onMouseLeave={e => {
                if (activeTab !== id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'rgba(238,240,242,0.38)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }
              }}
            >
              <Icon size={15} style={s.navIcon(activeTab === id)} />
              {!collapsed && label}
            </button>
          ))}
        </nav>

        {/* Bottom actions */}
        <div style={{ padding: '12px 0', borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
          <Link
            to="/"
            style={{
              ...s.navItem(false, collapsed),
              display: 'flex', alignItems: 'center',
              gap: collapsed ? 0 : '12px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              textDecoration: 'none', fontSize: '10px',
            }}
            title={collapsed ? 'View site' : undefined}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = 'rgba(238,240,242,0.7)'; e.currentTarget.style.transform = 'translateX(2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(238,240,242,0.38)'; e.currentTarget.style.transform = 'translateX(0)'; }}
          >
            <ChevronLeft size={15} style={{ flexShrink: 0 }} />
            {!collapsed && 'View Site'}
          </Link>
          <button
            onClick={handleLogout}
            style={{ ...s.navItem(false, collapsed), color: 'rgba(200,100,100,0.6)' }}
            title={collapsed ? 'Logout' : undefined}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(200,80,80,0.08)'; e.currentTarget.style.color = 'rgba(220,120,120,0.9)'; e.currentTarget.style.transform = 'translateX(2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(200,100,100,0.6)'; e.currentTarget.style.transform = 'translateX(0)'; }}
          >
            <LogOut size={15} style={{ flexShrink: 0 }} />
            {!collapsed && 'Logout'}
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden" style={{ position:'fixed', top:0, left:0, right:0, zIndex:50, background:'rgba(13,15,20,0.97)', backdropFilter:'blur(16px)', borderBottom:'0.5px solid rgba(255,255,255,0.07)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px' }}>
          <span style={{ fontFamily:'var(--font-display)', fontSize:'18px', letterSpacing:'0.04em', color:'var(--swp-white)' }}>{activeLabel}</span>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(238,240,242,0.5)' }}>
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div style={{ borderTop:'0.5px solid rgba(255,255,255,0.07)', paddingBottom:'12px' }}>
            {TABS.map(({ id, label, Icon }) => (
              <button key={id} onClick={() => { handleTabChange(id); setMobileMenuOpen(false); }}
                style={{ display:'flex', alignItems:'center', gap:'12px', width:'calc(100% - 24px)', margin:'2px 12px', padding:'10px 16px', background: activeTab === id ? 'rgba(106,157,190,0.12)' : 'transparent', border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'var(--font-mono)', fontSize:'10px', letterSpacing:'0.13em', textTransform:'uppercase', color: activeTab === id ? 'var(--swp-ice)' : 'rgba(238,240,242,0.4)', transition:'all 0.2s' }}>
                <Icon size={15} /> {label}
              </button>
            ))}
            <div style={{ borderTop:'0.5px solid rgba(255,255,255,0.07)', padding:'12px 20px', marginTop:'8px' }}>
              <Link to="/" style={{ display:'flex', alignItems:'center', gap:'8px', textDecoration:'none', fontFamily:'var(--font-mono)', fontSize:'10px', letterSpacing:'0.13em', textTransform:'uppercase', color:'rgba(238,240,242,0.4)', marginBottom:'12px' }}>
                <ChevronLeft size={14} /> View Site
              </Link>
              <button onClick={handleLogout} style={{ display:'flex', alignItems:'center', gap:'8px', background:'none', border:'none', cursor:'pointer', fontFamily:'var(--font-mono)', fontSize:'10px', letterSpacing:'0.13em', textTransform:'uppercase', color:'rgba(200,80,80,0.7)' }}>
                <LogOut size={14} /> Logout
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main area */}
      <div style={s.main} className="pt-14 lg:pt-0">
        {/* Top bar */}
        <div style={s.topbar}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '20px', letterSpacing: '0.04em', color: 'var(--swp-white)' }}>
            {activeLabel}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(238,240,242,0.25)' }}>
            Admin
          </span>
        </div>

        {/* Tab content */}
        <div style={s.content}>
          {renderTab()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
